import { useState } from "react";
import { Loader2, Pause } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { pauseJob } from "@/api/engine";
import type { JobConfig } from "@/api/types";

interface PauseModalProps {
  job: JobConfig | null;
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (updated: JobConfig) => void;
}

export function PauseModal({
  job,
  projectSlug,
  open,
  onOpenChange,
  onSuccess,
}: PauseModalProps) {
  const [busy, setBusy] = useState(false);

  async function confirm() {
    if (!job) return;
    setBusy(true);
    try {
      const updated = await pauseJob(projectSlug, job.job_id);
      toast.success(`${job.name} paused`);
      onSuccess(updated);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-amber-100">
            <Pause className="h-4 w-4 text-amber-600" />
          </div>
          <DialogTitle>Pause this job?</DialogTitle>
          <DialogDescription className="leading-relaxed">
            {job ? (
              <>
                <span className="font-medium text-slate-700">{job.name}</span>{" "}
                will stop running on its schedule until you resume it. Manual
                triggers stay available.
              </>
            ) : (
              "Job will stop running on its schedule."
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={busy}
            className="bg-amber-600 text-white hover:bg-amber-500"
          >
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Pause
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't pause the job.";
}
